import { C_ACCOUNT } from '_acaSrc/utility/constants';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import { reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { SET_MERGED_ACCOUNT_INFO } from '_acaSrc/store/login.store';
import MergeResultHandler from './merge-accounts/MergeResultHandler';
import NavigationService from './merge-accounts/NavigationService';
import AccountDataService from './merge-accounts/AccountDataService';
import StatusHandler from './merge-accounts/StatusHandler';
import ValidationService from './merge-accounts/ValidationService';
import ErrorHandler from './merge-accounts/ErrorHandler';
import UtdCache from '_acaSrc/utility/UtdCache';

let mergeAccountsInstance;

export default function useMergeAccounts() {
    if (mergeAccountsInstance) {
        return mergeAccountsInstance;
    }

    const userName = ref('');
    const password = ref('');
    const isSubmitting = ref(false);
    const isMerging = ref(false);

    const mergeData = reactive({
        error: false,
        consolidateSuccess: false,
        findAccountSuccess: false,
        accountToBeDeactivated: null,
        accountRemainsActive: null,
        mergeType: null,
        totalCredits: null
    });

    const mergeError = reactive({
        message: '',
        title: ''
    });

    const store = useStore();
    const navigationService = new NavigationService(store);

    const accountGetters = {
        getOrgData: () => AccountDataService.extractAccountData(
            mergeData.accountToBeDeactivated
        ),
        getActiveData: () => AccountDataService.extractAccountData(
            mergeData.accountRemainsActive
        ),
        getOrgEmail: () => mergeData.accountToBeDeactivated?.email?.emailAddress,
        getActiveEmail: () => mergeData.accountRemainsActive?.email?.emailAddress,
        getOrgCredits: () => mergeData.accountToBeDeactivated?.credits,
        getActiveCredits: () => mergeData.accountRemainsActive?.credits,
        getTotalCredits: () => mergeData.totalCredits,
        getOrgCmeType: () => mergeData.accountToBeDeactivated?.cmeType,
        getActiveCmeType: () => mergeData.accountRemainsActive?.cmeType,
        getOrgName: () => mergeData.accountToBeDeactivated?.name,
        getActiveName: () => mergeData.accountRemainsActive?.name,
        getActiveUserName: () => mergeData.accountRemainsActive?.userName
    };

    const setSubmitting = (value) => {
        isSubmitting.value = value;
    };

    const clearError = () => {
        mergeError.message = '';
        mergeError.title = '';
        mergeData.error = false;
    };

    const setErrorMessage = (errorMessage, errorTitle) => {
        const mappedTitle = ErrorHandler.getMappedErrorTitle(errorMessage, errorTitle);
        const defaultMessage = C_ACCOUNT.FORM_MESSAGING.FIND_ACCOUNT_DEFAULT_MESSAGE;
        mergeError.title = mappedTitle;
        mergeError.message = errorMessage || defaultMessage;
        mergeData.error = true;
    };

    const resetMerge = () => {
        mergeError.message = '';
        mergeError.title = '';
        mergeData.error = false;
        mergeData.consolidateSuccess = false;
        mergeData.findAccountSuccess = false;
        mergeData.accountToBeDeactivated = null;
        mergeData.accountRemainsActive = null;
        mergeData.totalCredits = null;
        mergeData.mergeType = null;
        isMerging.value = false;
        userName.value = '';
        password.value = '';
    };

    const handleSuccessfulFind = (response) => {
        mergeData.error = false;
        mergeData.accountToBeDeactivated = response.accountToBeDeactivated;
        mergeData.accountRemainsActive = response.accountRemainsActive;
        mergeData.findAccountSuccess = true;
        if (response.mergeType === C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.SSO) {
            mergeData.mergeType = C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.NON_SSO_AND_SSO;
        }
        else {
            mergeData.mergeType = response.mergeType;
        }
        mergeData.totalCredits = response.totalCredits;
        
        if (response.mergeProfile) {
            store.commit(
                `login/${SET_MERGED_ACCOUNT_INFO}`, 
                { mergeProfile: response.mergeProfile }, 
                { root: true }
            );
        }
        
        mergeError.message = '';
        mergeError.title = '';
    };

    const handleSsoMergeAttempt = (oidcMergePreviewSummary) => {
        if (!oidcMergePreviewSummary) {
            return;
        }

        const isNewerWinner = oidcMergePreviewSummary.isNewerAccountWinner;
        const winnerCredits = isNewerWinner 
            ? oidcMergePreviewSummary.newerAccountCredits 
            : oidcMergePreviewSummary.olderAccountCredits;
        const loserCredits = isNewerWinner 
            ? oidcMergePreviewSummary.olderAccountCredits 
            : oidcMergePreviewSummary.newerAccountCredits;

        const winnerAccount = {
            credits: winnerCredits,
            name: oidcMergePreviewSummary.name,
            email: {
                emailAddress: oidcMergePreviewSummary.email
            },
            userName: oidcMergePreviewSummary.userName || '',
            cmeType: null
        };

        const loserAccount = {
            credits: loserCredits,
            name: '',
            email: {
                emailAddress: ''
            },
            userName: '',
            cmeType: null
        };

        mergeData.error = false;
        mergeData.findAccountSuccess = true;
        mergeData.accountRemainsActive = winnerAccount;
        mergeData.accountToBeDeactivated = loserAccount;
        mergeData.mergeType = getDerivedMergeType(oidcMergePreviewSummary.mergeType);
        mergeData.totalCredits = oidcMergePreviewSummary.totalCredits || 0;
        mergeError.message = '';
        mergeError.title = '';
    };

    const getDerivedMergeType = (mergeType) => {
        switch (mergeType) {
        case C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.SSO:
            return C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.SSO_AND_SSO;
        case C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.NON_SSO:
            return C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.NON_SSO_AND_SSO;
        case C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.SSO_WINNER:
            return C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.TARGET_SSO_WINNER;
        default:
            return C_ACCOUNT.FORM_MESSAGING.MERGE_TYPES.INDIVIDUAL;
        }
    };

    const handleFindAccountError = (error) => {
        mergeData.error = true;
        const errors = error?.data?.errors || [];
        errors.forEach((err) => {
            setErrorMessage(err.message, err.title);
        });
    };

    const canPerformMerge = () => {
        if (isMerging.value) {
            return false;
        }
        
        isMerging.value = true;
        clearError();

        const validation = ValidationService.validateCredentials(
            userName.value, 
            password.value
        );
        if (!validation.isValid) {
            isMerging.value = false;
            return false;
        }
        
        return true;
    };

    const performAutoMerge = async() => {
        if (!canPerformMerge()) {
            return false;
        }
        const utdCache = new UtdCache();

        const credentials = {
            userName: userName.value,
            password: password.value,
            saveUserName: false,
            isSubmitting: false
        };

        try {
            const response = await utdRest('auth/login', credentials);
            Object.assign(mergeData, response);
            utdCache.removePersistent('ssoRedirectURL');
            return true;
        }
        catch (error) {
            handleFindAccountError(error);
            return false;
        }
        finally {
            isMerging.value = false;
        }
    };

    const findConsolidateAccount = async(userInfo) => {
        if (!canPerformMerge()) {
            return false;
        }

        try {
            const response = await utdRest('getConsolidateAccounts', userInfo);
            StatusHandler.handleStatus(
                response?.status,
                response,
                handleSuccessfulFind,
                handleFindAccountError
            );
            return true;
        }
        catch (error) {
            const formattedErrors = ErrorHandler.formatConsolidateError(error);
            handleFindAccountError(formattedErrors);
            return false;
        }
        finally {
            isMerging.value = false;
        }
    };

    const consolidateAccounts = async() => {
        if (isMerging.value) {
            return MergeResultHandler.createErrorResult();
        }

        isMerging.value = true;
        clearError();

        try {
            await utdRest('mergeAccounts', undefined);
            mergeData.error = false;
            mergeData.consolidateSuccess = true;
            return MergeResultHandler.createSuccessResult();
        }
        catch (error) {
            mergeData.error = true;
            handleFindAccountError(error);
            return MergeResultHandler.createErrorResult();
        }
        finally {
            isMerging.value = false;
        }
    };

    mergeAccountsInstance = {
        userName,
        password,
        isMerging,
        mergeData,
        mergeError,
        isSubmitting,
        setSubmitting,
        resetMerge,
        clearError,
        performAutoMerge,
        findConsolidateAccount,
        consolidateAccounts,
        handleSsoMergeAttempt,
        handleSuccessfulFind,
        
        navigateToSso: (ssoUrl, regMergeSource) => 
            navigationService.navigateToSso(ssoUrl, regMergeSource, setSubmitting),

        ...accountGetters,

        handleConsolidateStatus: (res, status) => 
            StatusHandler.handleStatus(
                status, 
                res, 
                handleSuccessfulFind, 
                handleFindAccountError
            ),
        handleFindAccountError,
        createErrorResponse: ErrorHandler.createErrorResponse,
        checkConsolidateErrorStatus: ErrorHandler.checkConsolidateErrorStatus
    };

    return mergeAccountsInstance;
}